import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useData } from "@/context/DataContext";
import AmmTabs from "@/components/AmmTabs";
import HiSocialLogo from "@/components/docs/HiSocialLogo";
import { exportElementToPdf } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Search, FileSignature, Trash2, Pencil, Download, Copy, Eye, Upload, FileText } from "lucide-react";
import { formatDate, todayISO } from "@/lib/tp";
import { toast } from "sonner";

const fmtCreated = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—");

function ContrattoDoc({ data, logoUrl = null }) {
  const d = data || {};
  return (
    <div style={{ width: 794, background: "#fff", color: "#18181b", padding: "48px 64px", fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
      <HiSocialLogo size="md" src={logoUrl} />
      <div style={{ width: 120, height: 6, borderRadius: 4, background: "linear-gradient(90deg,#ec0f8e,#a21caf)", margin: "22px 0 16px" }} />
      <h1 style={{ fontSize: 30, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>{d.title || "Contratto"}</h1>
      <div style={{ fontSize: 16, color: "#52525b", marginTop: 6 }}>
        {d.client_name || "—"}{d.date ? ` · ${formatDate(d.date)}` : ""}
      </div>
      <div style={{ borderTop: "1px solid #e4e4e7", margin: "20px 0" }} />
      <div style={{ fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap", color: "#27272a" }}>{d.body}</div>
      <div style={{ marginTop: 28, paddingTop: 14, borderTop: "1px solid #e4e4e7", fontSize: 12, color: "#71717a", display: "flex", justifyContent: "space-between" }}>
        <span>HISOCIAL ITALIA · Social Media Strategies</span>
        <span>hisocialitalia.it</span>
      </div>
    </div>
  );
}

const CONTRACT_TEMPLATE = `CONTRATTO DI COLLABORAZIONE PROFESSIONALE

TRA
I professionisti autonomi Lorenzo Babini, Alessio Calise e Stefano Costantini, ciascuno titolare di autonoma Partita IVA, operanti congiuntamente sotto il marchio/nome commerciale "HISOCIAL / HI SOCIAL ITALIA", di seguito denominati "Consulenti".
- LORENZO BABINI, Corso Giuseppe Mazzini 223, San Benedetto del Tronto (AP) 63074 — P.IVA [___] — C.F. [___]
- ALESSIO CALISE, Via Verga n. 8, 63076 Monteprandone (AP) — P.IVA [___] — C.F. [___]
- STEFANO COSTANTINI, Via Piemonte n. 91, 63074 San Benedetto del Tronto (AP) — P.IVA [___] — C.F. [___]

E
[NOME CLIENTE] — P.IVA [P.IVA CLIENTE]
[INDIRIZZO CLIENTE] — Codice univoco [CODICE UNIVOCO]

SI CONVIENE E SI STIPULA QUANTO SEGUE

PREMESSA
Le Parti danno atto che il presente contratto non comporta la costituzione di una società tra i Consulenti, né nei rapporti interni né nei confronti del Cliente. Ciascun Consulente conserva autonomia professionale, organizzativa, fiscale e contabile.

ART. 1 - RAPPORTO
Le parti si danno reciprocamente atto che viene tra loro stipulato un contratto di collaborazione di lavoro autonomo consistente nello svolgimento di una attività temporanea con le modalità ed i termini di seguito convenuti. I Social Media Manager si impegneranno utilizzando tutti i mezzi di cui sono a conoscenza per potenziare la presenza sui Social Media del Cliente.

ART. 2 - OGGETTO DELLA PRESTAZIONE
Fornitura di un pacchetto di servizi volto a stabilire una presenza del Cliente sui canali social al fine di accrescere la conoscenza del brand, migliorare la reputazione, generare leads e favorire la crescita organica. Piattaforme: Facebook, Instagram.
In particolare i Consulenti offrono:
- Pubblicazione fino a [N] post mensili per [N] mesi (gestione full) e [N]/mese per gli altri [N] mesi.
- Editing video e contenuti.
- Realizzazione campagne pubblicitarie (budget ADV escluso dal contratto).
- [N] incontri mensili presso la sede del Consulente o da concordare.
- Setup iniziale per il settaggio delle pagine e l'accesso ai consulenti.
- Campagne foto e video al costo di [___]€ cadauna (consigliato 1 ogni 3 mesi).

ART. 3 - MODALITÀ DI ESECUZIONE
Le prestazioni non determinano rapporto di subordinazione. I Consulenti hanno piena autonomia organizzativa nell'ambito delle direttive generali del Cliente.

ART. 4 - DURATA DEL CONTRATTO
L'incarico avrà inizio il [DATA INIZIO] e si concluderà il [DATA FINE]. Salvo diverso accordo scritto, il contratto si intende tacitamente rinnovato per eguale durata, salvo disdetta via raccomandata A/R o PEC almeno 30 giorni prima della scadenza. In caso di rescissione anticipata, penale pari a [N] mensilità ([___]€).

ART. 5 - COMPENSO E MODALITÀ DI PAGAMENTO
Compenso complessivo: Euro [IMPORTO TOTALE]. Pagamento in 3 rate: [RATA 1] alla partenza, [RATA 2] entro il [DATA], [RATA 3] entro il [DATA]. Spese extra: [___]€ setup iniziale, [___]€ servizio GRAFICHE, da saldare nella prima tranche. In caso di mancato pagamento entro 5 giorni dalla scadenza, i Consulenti potranno sospendere i servizi.

ART. 6 - PROPRIETÀ E RISERVATEZZA DEI RISULTATI
I contenuti prodotti sono di esclusiva proprietà del Committente. I Consulenti possono mostrarli in presentazioni, corsi e incontri con potenziali clienti. Tutti i dati tecnico-amministrativi sono riservati.

ART. 7 - RESPONSABILITÀ
Il Cliente esonera i Consulenti da responsabilità per danni derivanti dall'esecuzione del contratto ed è titolare e responsabile dei dati pubblicati. Ciascun Consulente risponde delle attività da lui direttamente svolte.

ART. 8 - SOSPENSIONE DEL SERVIZIO
I Consulenti si riservano il diritto di sospendere/interrompere il servizio in caso di violazioni del contratto o della normativa vigente (incluso GDPR UE 2016/679), dandone comunicazione via e-mail.

ART. 9 - ACCESSI E AUTORIZZAZIONI
Il Cliente autorizza i Consulenti ad accedere e operare sui profili social, account pubblicitari e piattaforme indicate, esclusivamente per le attività oggetto del contratto.

ART. 10 - MODIFICHE E AGGIUNTE
Ogni modifica deve risultare da atto scritto firmato da entrambe le parti, con preavviso di almeno 48/60 ore (esclusi festivi, sabato e domenica).

ART. 11 - FORZA MAGGIORE
I Consulenti non sono responsabili per ritardi o inadempimenti dovuti a caso fortuito o forza maggiore.

ART. 12 - COMUNICAZIONI
Comunicazioni via e-mail a: [EMAIL]. Disponibilità nei giorni lavorativi. Presa in carico entro 3 giorni lavorativi. Non si accettano SMS, chat, WhatsApp.

ART. 13 - NORME DI RINVIO
Per quanto non disciplinato si applicano gli artt. 2229 e segg. del codice civile.

ART. 14 - PRIVACY
Trattamento dati ai sensi del Regolamento UE 2016/679 (GDPR) per l'esecuzione del contratto e gli adempimenti di legge. Diritti artt. 15 e segg. esercitabili all'indirizzo [EMAIL].

ART. 15 - FORO COMPETENTE
Per le controversie è competente in via esclusiva il Foro di [FORO].

ART. 16 - CLAUSOLA GENERALE
Il presente contratto sostituisce ogni precedente intesa. Qualsiasi modifica è valida se scritta e firmata dalle parti.

SERVIZI
- Gestione Grafiche e Creatività — Pacchetto [___]€
- Landing Page Promozionale — Opzionale [___]€
- Video Corporate — Opzionale [___]€
- Shooting Fotografico (3h) — Opzionale [___]€
- Shooting Video / Reel (3h) — Opzionale [___]€
- Shooting Video + Foto (3h) — Opzionale [___]€
- Shooting Video + Foto + Drone (3h) — Opzionale [___]€
- Presenza Operatore agli Eventi — Opzionale [___]€

Ai sensi degli artt. 1341 e 1342 c.c., il Cliente approva specificamente le clausole: Art. 4, 5, 7, 8, 10, 12, 15.

Il Cliente ______________________    Luogo e data: ______________________

I CONSULENTI
Lorenzo Babini ____________   Alessio Calise ____________   Stefano Costantini ____________`;

function DocLogo() {
  return (
    <div className="flex items-center gap-2">
      <span className="h-9 w-9 rounded-xl flex items-center justify-center text-white font-bold text-lg" style={{ background: "linear-gradient(135deg,#d946ef,#ec4899)" }}>H</span>
      <div className="leading-tight">
        <div className="font-bold tracking-tight text-fuchsia-400 text-lg">HISOCIAL</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-400">Social Media Strategies</div>
      </div>
    </div>
  );
}

const emptyForm = () => ({
  id: null, client_name: "", date: todayISO(),
  title: "Contratto di Collaborazione professionale", body: CONTRACT_TEMPLATE, status: "bozza",
});

export default function AmmContratti() {
  const { clients } = useData();
  const logoUrl = clients.find((c) => c.id === "admin-section")?.logo_url || null;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [docData, setDocData] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const pdfRef = useRef(null);

  // Upload PDF esterno
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadForm, setUploadForm] = useState({ client_name: "", file_name: "", file: null });
  const [uploading, setUploading] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [filePreviewOpen, setFilePreviewOpen] = useState(false);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const inputCls = "mt-1 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-fuchsia-500 outline-none";

  const fetchBlobUrl = async (c) => {
    const { data } = await api.get(`/contratti/${c.id}/file`, { responseType: "blob" });
    return URL.createObjectURL(data);
  };

  const submitUpload = async () => {
    if (!uploadForm.file) return toast.error("Seleziona un file PDF");
    if (!uploadForm.client_name.trim()) return toast.error("Inserisci il nome cliente");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadForm.file);
      fd.append("client_name", uploadForm.client_name);
      fd.append("file_name", uploadForm.file_name || uploadForm.file.name);
      await api.post("/contratti/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadOpen(false);
      setUploadForm({ client_name: "", file_name: "", file: null });
      toast.success("PDF caricato");
      load();
    } catch { toast.error("Errore caricamento PDF"); }
    finally { setUploading(false); }
  };

  const openFilePreview = async (c) => {
    try { const url = await fetchBlobUrl(c); setFilePreviewUrl(url); setFilePreviewOpen(true); }
    catch { toast.error("Errore apertura PDF"); }
  };

  const downloadFile = async (c) => {
    try {
      const url = await fetchBlobUrl(c);
      const a = document.createElement("a");
      a.href = url; a.download = c.file_name || `${c.client_name || "documento"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Errore download PDF"); }
  };

  const openRename = (c) => { setRenameTarget(c); setRenameValue((c.file_name || "").replace(/\.pdf$/i, "")); setRenameOpen(true); };
  const submitRename = async () => {
    if (!renameValue.trim()) return;
    try {
      await api.patch(`/contratti/${renameTarget.id}/meta`, { file_name: renameValue.trim() });
      setRenameOpen(false);
      toast.success("Rinominato");
      load();
    } catch { toast.error("Errore rinomina"); }
  };

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/contratti"); setList(data); }
    catch { toast.error("Errore caricamento contratti"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) =>
      (c.client_name || "").toLowerCase().includes(q) ||
      (c.number || "").toLowerCase().includes(q) ||
      (c.file_name || "").toLowerCase().includes(q) ||
      (c.title || "").toLowerCase().includes(q));
  }, [list, search]);

  const openNew = () => { setForm(emptyForm()); setOpen(true); };
  const openEdit = (c) => { setForm({ ...emptyForm(), ...c }); setOpen(true); };

  const save = async () => {
    if (!form.client_name.trim()) return toast.error("Inserisci il nome cliente");
    setSaving(true);
    try {
      const payload = { client_name: form.client_name, date: form.date || null, title: form.title, body: form.body, status: form.status };
      if (form.id) await api.patch(`/contratti/${form.id}`, payload);
      else await api.post("/contratti", payload);
      setOpen(false);
      toast.success("Contratto salvato");
      load();
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Eliminare questo contratto?")) return;
    await api.delete(`/contratti/${id}`);
    toast.success("Contratto eliminato");
    load();
  };

  const duplicate = async (c) => {
    await api.post("/contratti", {
      client_name: `${c.client_name} (copia)`, date: c.date || null,
      title: c.title, body: c.body, status: "bozza",
    });
    toast.success("Contratto duplicato");
    load();
  };

  const downloadPdf = (c) => {
    setDocData(c);
    setTimeout(async () => {
      try {
        await exportElementToPdf(
          pdfRef.current,
          `Contratto-${(c.client_name || "cliente").replace(/\s+/g, "_")}.pdf`,
          { pageNumbers: true, headerText: `${c.title || "Contratto"} · ${c.client_name || ""}` },
        );
      } catch { toast.error("Errore generazione PDF"); }
      setDocData(null);
    }, 400);
  };

  const openPreview = (c) => { setPreviewData(c); setPreviewOpen(true); };

  return (
    <div className="space-y-6 tp-fade-in">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Interna</div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">Amministrazione</h1>
      </div>
      <AmmTabs />

      <section className="tp-glass rounded-3xl overflow-hidden">
        <div className="px-5 sm:px-6 py-4 border-b border-white/5 flex items-center gap-3 flex-wrap">
          <FileSignature size={15} className="text-fuchsia-400" />
          <h2 className="text-base font-semibold text-white">Contratti</h2>
          <div className="relative ml-auto">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input data-testid="contratti-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca cliente o contratto..." className="pl-9 h-9 w-56 bg-white/5 border-white/10 text-white" />
          </div>
          <Button data-testid="upload-contratto-button" onClick={() => setUploadOpen(true)} variant="outline"
            className="h-9 rounded-full px-3 border-white/10 text-zinc-300 hover:bg-white/5">
            <Upload size={14} className="mr-1.5" /> Carica PDF
          </Button>
          <Button data-testid="add-contratto-button" onClick={openNew} className="tp-brand-btn h-9 rounded-full px-3">
            <Plus size={14} className="mr-1.5" /> Contratto
          </Button>
        </div>
        <div className="p-4 space-y-2">
          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-zinc-500">Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-zinc-500 italic">Nessun contratto. Creane uno con "Contratto".</div>
          ) : (
            filtered.map((c) => (
              <div key={c.id} data-testid={`contratto-row-${c.id}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${c.kind === "upload" ? "bg-white/10 border border-white/10" : "tp-brand-gradient"}`}>
                  {c.kind === "upload" ? <FileText size={16} className="text-fuchsia-300" /> : <FileSignature size={16} className="text-white" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-white font-medium truncate flex items-center gap-2">
                    {c.kind === "upload" ? (c.file_name || c.client_name) : c.client_name}
                    {c.kind === "upload" && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-400/20">PDF</span>}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{c.number} · {c.kind === "upload" ? c.client_name : c.title}</div>
                </div>
                <div className="text-xs text-zinc-400 hidden sm:block whitespace-nowrap">Creato: {fmtCreated(c.created_at)}</div>
                {c.kind === "upload" ? (
                  <>
                    <button onClick={() => openFilePreview(c)} title="Anteprima" className="p-2 rounded-lg text-zinc-400 hover:text-fuchsia-300 hover:bg-white/5" data-testid={`contratto-preview-${c.id}`}><Eye size={15} /></button>
                    <button onClick={() => downloadFile(c)} title="Scarica PDF" className="p-2 rounded-lg text-zinc-400 hover:text-fuchsia-300 hover:bg-white/5" data-testid={`contratto-pdf-${c.id}`}><Download size={15} /></button>
                    <button onClick={() => openRename(c)} title="Rinomina" className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5" data-testid={`contratto-rename-${c.id}`}><Pencil size={14} /></button>
                  </>
                ) : (
                  <>
                    <button onClick={() => openPreview(c)} title="Anteprima" className="p-2 rounded-lg text-zinc-400 hover:text-fuchsia-300 hover:bg-white/5" data-testid={`contratto-preview-${c.id}`}><Eye size={15} /></button>
                    <button onClick={() => downloadPdf(c)} title="Scarica PDF" className="p-2 rounded-lg text-zinc-400 hover:text-fuchsia-300 hover:bg-white/5" data-testid={`contratto-pdf-${c.id}`}><Download size={15} /></button>
                    <button onClick={() => duplicate(c)} title="Duplica" className="p-2 rounded-lg text-zinc-400 hover:text-fuchsia-300 hover:bg-white/5" data-testid={`contratto-duplicate-${c.id}`}><Copy size={14} /></button>
                    <button onClick={() => openEdit(c)} className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5" data-testid={`contratto-edit-${c.id}`}><Pencil size={14} /></button>
                  </>
                )}
                <button onClick={() => remove(c.id)} className="p-2 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-red-500/10" data-testid={`contratto-delete-${c.id}`}><Trash2 size={14} /></button>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Light editor */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl p-0 tp-glass-strong text-white border-white/10 max-h-[92vh] overflow-y-auto" data-testid="contratto-editor">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10 flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-white flex items-center gap-2"><FileSignature size={18} className="text-fuchsia-400" /> {form.id ? "Modifica contratto" : "Nuovo contratto"}</DialogTitle>
            <div className="flex items-center gap-3">
              <button onClick={() => openPreview(form)} className="text-zinc-500 hover:text-fuchsia-400 flex items-center gap-1 text-sm" data-testid="contratto-editor-preview"><Eye size={15} /> Anteprima</button>
              <button onClick={() => downloadPdf(form)} className="text-zinc-500 hover:text-fuchsia-400 flex items-center gap-1 text-sm" data-testid="contratto-editor-pdf"><Download size={15} /> PDF</button>
            </div>
          </DialogHeader>

          <div className="px-6 py-5 space-y-5 tp-doc-light">
            <DocLogo />
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="text-xs text-zinc-500">Cliente</span>
                <input data-testid="contratto-client" value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-zinc-900 focus:border-fuchsia-500 outline-none" placeholder="Nome cliente" />
              </label>
              <label className="text-sm">
                <span className="text-xs text-zinc-500">Data</span>
                <input type="date" value={form.date || ""} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-zinc-900 focus:border-fuchsia-500 outline-none" />
              </label>
            </div>
            <label className="text-sm block">
              <span className="text-xs text-zinc-500">Titolo</span>
              <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-2 text-zinc-900 focus:border-fuchsia-500 outline-none" />
            </label>
            <label className="text-sm block">
              <span className="text-xs text-zinc-500">Testo contratto (modificabile)</span>
              <textarea data-testid="contratto-body" value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} rows={22}
                className="mt-1 w-full rounded-lg border border-zinc-300 px-3 py-3 text-sm leading-relaxed text-zinc-800 focus:border-fuchsia-500 outline-none font-mono" />
            </label>
            <div className="pt-3 border-t border-zinc-200 flex items-center justify-between text-[11px] text-zinc-400">
              <span>HISOCIAL ITALIA · Social Media Strategies</span>
              <span>Documento generato con Hi-Work.</span>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-2 sticky bottom-0 tp-glass-strong">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10 text-zinc-200">Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="contratto-save" className="tp-brand-btn">{saving ? "Salvo…" : "Salva contratto"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Anteprima documento a schermo */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-[860px] p-0 bg-zinc-100 border-zinc-300 max-h-[92vh] overflow-y-auto" data-testid="contratto-preview-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/10 flex-row items-center justify-between space-y-0 sticky top-0 bg-zinc-100 z-10">
            <DialogTitle className="text-white flex items-center gap-2"><Eye size={18} className="text-fuchsia-400" /> Anteprima contratto</DialogTitle>
            <button onClick={() => previewData && downloadPdf(previewData)} className="text-sm text-fuchsia-400 hover:underline flex items-center gap-1" data-testid="contratto-preview-download"><Download size={14} /> Scarica PDF</button>
          </DialogHeader>
          <div className="p-5 flex justify-center">
            <div className="shadow-xl">{previewData && <ContrattoDoc data={previewData} logoUrl={logoUrl} />}</div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Off-screen render target for PDF export */}
      <div style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }} aria-hidden="true">
        <div ref={pdfRef}>{docData && <ContrattoDoc data={docData} logoUrl={logoUrl} />}</div>
      </div>

      {/* Upload PDF esterno */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-w-md p-0 tp-glass-strong text-white border-white/10" data-testid="contratto-upload-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10">
            <DialogTitle className="text-white flex items-center gap-2"><Upload size={18} className="text-fuchsia-400" /> Carica contratto (PDF)</DialogTitle>
          </DialogHeader>
          <div className="px-6 py-5 space-y-4">
            <label className="text-sm block"><span className="text-xs text-zinc-500">Cliente</span>
              <input data-testid="upload-client" value={uploadForm.client_name} onChange={(e) => setUploadForm((f) => ({ ...f, client_name: e.target.value }))} className={inputCls} placeholder="Nome cliente" /></label>
            <label className="text-sm block"><span className="text-xs text-zinc-500">Nome file (opzionale)</span>
              <input data-testid="upload-name" value={uploadForm.file_name} onChange={(e) => setUploadForm((f) => ({ ...f, file_name: e.target.value }))} className={inputCls} placeholder="Es. Contratto firmato" /></label>
            <label className="text-sm block"><span className="text-xs text-zinc-500">File PDF</span>
              <input data-testid="upload-file" type="file" accept="application/pdf" onChange={(e) => setUploadForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
                className="mt-1 w-full text-sm text-zinc-700 file:mr-3 file:rounded-lg file:border-0 file:bg-fuchsia-50 file:px-3 file:py-2 file:text-fuchsia-700 hover:file:bg-fuchsia-100" /></label>
          </div>
          <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setUploadOpen(false)} className="border-white/10 text-zinc-200">Annulla</Button>
            <Button onClick={submitUpload} disabled={uploading} data-testid="upload-submit" className="tp-brand-btn">{uploading ? "Carico…" : "Carica PDF"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rinomina PDF */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-sm p-0 tp-glass-strong text-white border-white/10" data-testid="contratto-rename-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-white/10">
            <DialogTitle className="text-white flex items-center gap-2"><Pencil size={16} className="text-fuchsia-400" /> Rinomina</DialogTitle>
          </DialogHeader>
          <div className="px-6 py-5">
            <input data-testid="rename-input" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} className={inputCls} placeholder="Nome file" autoFocus />
          </div>
          <div className="px-6 py-4 border-t border-zinc-200 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRenameOpen(false)} className="border-white/10 text-zinc-200">Annulla</Button>
            <Button onClick={submitRename} data-testid="rename-submit" className="tp-brand-btn">Salva</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Anteprima file PDF caricato */}
      <Dialog open={filePreviewOpen} onOpenChange={(o) => { setFilePreviewOpen(o); if (!o && filePreviewUrl) { URL.revokeObjectURL(filePreviewUrl); setFilePreviewUrl(null); } }}>
        <DialogContent className="max-w-[900px] p-0 bg-zinc-100 border-zinc-300" data-testid="contratto-file-preview-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-white/10">
            <DialogTitle className="text-white flex items-center gap-2"><Eye size={18} className="text-fuchsia-400" /> Anteprima PDF</DialogTitle>
          </DialogHeader>
          <div className="p-3">
            {filePreviewUrl && <iframe title="pdf" src={filePreviewUrl} className="w-full h-[75vh] rounded-lg bg-white" />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
